import React, { useState, useEffect } from 'react';
import { useScrollLock } from '../../hooks/useScrollLock';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';




const Sidebar = ({ isCollapsed = false, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, userProfile, isSuperAdmin, activeCompany } = useAuth();
  const { t, i18n } = useTranslation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useScrollLock(isMobileOpen);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const navigationItems = [
    {
      section: t('sidebar.main'),
      items: [
        { label: t('sidebar.dashboard'), path: '/dashboard', icon: 'LayoutDashboard' }
      ]
    },
    {
      section: t('sidebar.catalogs'),
      items: [
        { label: t('sidebar.pdfUpload'), path: '/pdf-upload', icon: 'Upload' },
        { label: t('sidebar.library'), path: '/pdf-library', icon: 'Library' },
        { label: t('sidebar.menuSettings'), path: '/menu-settings', icon: 'Menu' }
      ]
    },
    {
      section: t('sidebar.settings'),
      items: [
        { label: t('sidebar.accountSettings'), path: '/account-settings', icon: 'Settings' },
        { label: t('sidebar.newCompany'), path: '/company-create', icon: 'PlusSquare', superAdminOnly: true }
      ]
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) {
      setIsMobileOpen(false);
    }
  };

  const isActive = (path) => location?.pathname === path;

  const sidebarContent = (
    <>
      <div className="sidebar-header">
        <div className="sidebar-logo overflow-hidden">
          {activeCompany?.logoUrl ? (
            <img
              src={activeCompany.logoUrl}
              alt={activeCompany.companyName}
              className="w-6 h-6 object-contain"
            />
          ) : (
            <Icon name="BookOpen" size={24} className="sidebar-logo-icon" />
          )}
        </div>
        <span className="sidebar-logo-text">{activeCompany?.companyName || 'CatalogFlip'}</span>
      </div>

      <UserContextHeader isCollapsed={isCollapsed} />

      <nav className="flex-1 overflow-y-auto py-6">
        {navigationItems?.map((section, sectionIndex) => (
          <div key={sectionIndex} className="mb-6">
            <div className="nav-section-title">{section?.section}</div>
            {section?.items
              ?.filter(item => !item.superAdminOnly || isSuperAdmin)
              ?.map((item, itemIndex) => (
                <div
                  key={itemIndex}
                  onClick={() => handleNavigation(item?.path)}
                  className={`nav-item ${isActive(item?.path) ? 'nav-item-active' : ''}`}
                >
                  <Icon name={item?.icon} size={20} className="nav-item-icon" />
                  <span className="nav-item-text">{item?.label}</span>
                </div>
              ))}
          </div>
        ))}
      </nav>

      {isSuperAdmin && <CompanySwitcher isCollapsed={isCollapsed} />}

      <LanguageSelector isCollapsed={isCollapsed} />
    </>
  );

  return (
    <>
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="mobile-nav-toggle"
        aria-label="Toggle navigation"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={20} />
      </button>

      {isMobileOpen && (
        <div
          className="mobile-nav-backdrop"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      <aside
        className={`
          fixed lg:fixed top-0 left-0 h-full bg-card border-r border-border z-40
          transition-all duration-250 ease-smooth
          ${isCollapsed ? 'sidebar-collapsed w-20' : 'w-[280px]'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {sidebarContent}
        </div>

        {/* Collapse / expand tab — desktop only */}
        <button
          onClick={() => onToggle(!isCollapsed)}
          className="hidden lg:flex absolute top-6 -right-3 w-6 h-6 items-center justify-center bg-card border border-border rounded-full shadow-sm hover:bg-muted transition-colors text-muted-foreground hover:text-foreground z-50"
          title={isCollapsed ? t('sidebar.expand') : t('sidebar.collapse')}
        >
          <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={12} />
        </button>
      </aside>
    </>
  );
};

const UserContextHeader = ({ isCollapsed }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const { signOut, userProfile, user } = useAuth();
  const { t } = useTranslation();

  const userDisplay = {
    name: userProfile?.full_name || user?.email || 'User',
    role: userProfile?.role || 'Member',
    company: userProfile?.companies?.company_name || '',
    initials: userProfile?.full_name
      ? userProfile?.full_name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()
      : (user?.email?.[0]?.toUpperCase() || 'U')
  };

  const handleSettingsClick = () => {
    navigate('/account-settings');
    setIsDropdownOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="relative">
      <div
        className="user-context-header cursor-pointer hover:bg-muted transition-base"
        onClick={() => !isCollapsed && setIsDropdownOpen(!isDropdownOpen)}
      >
        <div className="user-avatar">{userDisplay?.initials}</div>
        <div className="user-info">
          <div className="user-name">{userDisplay?.name}</div>
          <div className="user-role">{userDisplay?.company}</div>
        </div>
        {!isCollapsed && (
          <Icon
            name="ChevronDown"
            size={16}
            className={`text-muted-foreground transition-transform duration-250 ${
              isDropdownOpen ? 'rotate-180' : ''
            }`}
          />
        )}
      </div>
      {isDropdownOpen && !isCollapsed && (
        <>
          <div
            className="fixed inset-0 z-30"
            onClick={() => setIsDropdownOpen(false)}
          />
          <div className="absolute top-full left-0 right-0 mt-1 mx-2 bg-popover border border-border rounded-md shadow-elevation-lg z-50 overflow-hidden">
            <div className="p-3 border-b border-border">
              <div className="text-sm font-medium text-foreground">{userDisplay?.name}</div>
              <div className="text-xs text-muted-foreground">{userDisplay?.role}</div>
            </div>
            <button
              onClick={handleSettingsClick}
              className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-muted transition-base flex items-center"
            >
              <Icon name="Settings" size={16} className="mr-2" />
              Account Settings
            </button>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-3 px-4 py-3 text-muted-foreground hover:text-foreground hover:bg-card/50 transition-colors w-full text-left rounded-lg"
            >
              <Icon name="LogOut" className="w-5 h-5" />
              {!isCollapsed && <span className="text-sm font-medium">{t('sidebar.signOut')}</span>}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const CompanySwitcher = ({ isCollapsed }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeCompany, allCompanies, setActiveCompany, isSuperAdmin } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  if (!isSuperAdmin) return null;

  const handleSelect = (companyId) => {
    setActiveCompany(companyId);
    setIsOpen(false);
    navigate('/dashboard');
  };

  return (
    <div className="relative border-t border-border pt-4 px-4 pb-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted rounded-lg transition-colors"
        title={isCollapsed ? (activeCompany?.companyName || t('companySwitcher.tooltip')) : undefined}
      >
        <div className="flex items-center justify-center w-5 h-5 flex-shrink-0">
          <Icon name="Building2" size={18} className="text-accent" />
        </div>
        {!isCollapsed && (
          <>
            <span className="text-sm font-medium text-foreground flex-1 text-left truncate">
              {activeCompany?.companyName || t('companySwitcher.noCompany')}
            </span>
            <Icon name="Shield" size={14} className="text-accent flex-shrink-0" />
            <Icon
              name="ChevronDown"
              size={16}
              className={`text-muted-foreground transition-transform duration-250 flex-shrink-0 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-30"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute bottom-full left-4 right-4 mb-2 bg-popover border border-border rounded-lg shadow-elevation-lg z-50 overflow-hidden max-h-64 overflow-y-auto">
            <div className="px-3 py-2 border-b border-border">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {t('companySwitcher.title')}
              </span>
            </div>
            {allCompanies.length === 0 ? (
              <div className="px-3 py-3 text-sm text-muted-foreground">
                {t('companySwitcher.empty')}
              </div>
            ) : (
              allCompanies.map((company) => (
                <button
                  key={company.id}
                  onClick={() => handleSelect(company.id)}
                  className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition-colors ${
                    company.id === activeCompany?.id
                      ? 'bg-accent/10 text-accent font-medium'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="Building2" size={14} />
                  <span className="truncate">{company.companyName}</span>
                  {company.id === activeCompany?.id && (
                    <Icon name="Check" size={14} className="ml-auto flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

const LanguageSelector = ({ isCollapsed }) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const languages = [
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'en', label: 'English', flag: '🇺🇸' }
  ];

  const currentLanguage = languages?.find(lang => lang?.code === i18n?.language) || languages?.[0];

  const handleLanguageChange = (langCode) => {
    i18n?.changeLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <div className="relative border-t border-border pt-4 px-4 pb-4">
      <button
        onClick={() => !isCollapsed && setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted rounded-lg transition-colors"
      >
        <div className="flex items-center justify-center w-5 h-5">
          <span className="text-lg">{currentLanguage?.flag}</span>
        </div>
        {!isCollapsed && (
          <>
            <span className="text-sm font-medium text-foreground flex-1 text-left">
              {currentLanguage?.label}
            </span>
            <Icon
              name="ChevronDown"
              size={16}
              className={`text-muted-foreground transition-transform duration-250 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </>
        )}
      </button>

      {isOpen && !isCollapsed && (
        <>
          <div
            className="fixed inset-0 z-30"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute bottom-full left-4 right-4 mb-2 bg-popover border border-border rounded-lg shadow-elevation-lg z-50 overflow-hidden">
            {languages?.map((lang) => (
              <button
                key={lang?.code}
                onClick={() => handleLanguageChange(lang?.code)}
                className={`w-full px-3 py-2 text-left text-sm flex items-center gap-3 transition-colors ${
                  lang?.code === currentLanguage?.code
                    ? 'bg-accent/10 text-accent font-medium' :'text-foreground hover:bg-muted'
                }`}
              >
                <span className="text-lg">{lang?.flag}</span>
                <span>{lang?.label}</span>
                {lang?.code === currentLanguage?.code && (
                  <Icon name="Check" size={16} className="ml-auto" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Sidebar;
